import React, { useState } from "react";
import { Share2, Copy, MessageCircle, Mail, Check, X } from "lucide-react";

/**
 * One-tap share dropdown — WhatsApp / Email / Copy.
 * Falls back to navigator.share() on mobile if available.
 */
export default function ShareMenu({ url, title = "", text = "", testid = "share-menu" }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const fullUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const msg = `${text || title}\n\n${fullUrl}`.trim();

  async function nativeShare() {
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url: fullUrl });
        return true;
      } catch {
        // user cancelled
      }
    }
    return false;
  }

  async function handleClick() {
    const did = await nativeShare();
    if (!did) setOpen((v) => !v);
  }

  function copy() {
    navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="relative inline-block" data-testid={testid}>
      <button
        type="button"
        onClick={handleClick}
        className="btn btn-ghost btn-sm"
        data-testid={`${testid}-trigger`}
      >
        <Share2 size={14} /> Share
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
          />
          <div
            className="absolute right-0 mt-2 w-64 z-50 cd-card p-2"
            style={{ background: "white" }}
            data-testid={`${testid}-popup`}
          >
            <div className="flex items-center justify-between px-2 py-1">
              <div className="overline">SHARE</div>
              <button onClick={() => setOpen(false)}><X size={14} /></button>
            </div>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(msg)}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-3 px-3 py-2 hover:bg-[color:var(--bg)]"
              data-testid={`${testid}-whatsapp`}
            >
              <MessageCircle size={16} style={{ color: "#25D366" }} />
              <span className="text-sm">WhatsApp</span>
            </a>
            <a
              href={`mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(msg)}`}
              className="flex items-center gap-3 px-3 py-2 hover:bg-[color:var(--bg)]"
              data-testid={`${testid}-email`}
            >
              <Mail size={16} />
              <span className="text-sm">Email</span>
            </a>
            <button
              type="button"
              onClick={copy}
              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-[color:var(--bg)] text-left"
              data-testid={`${testid}-copy`}
            >
              {copied ? <Check size={16} style={{ color: "var(--orange)" }} /> : <Copy size={16} />}
              <span className="text-sm">{copied ? "Copied!" : "Copy link"}</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
